import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { NumericTextField } from '@/components';
import type { ParamRange } from '@/api/trainingDataApi';

interface CoreODGroupProps {
  coreOdMin: number | null;
  coreOdMax: number | null;
  hotOd: number | null;
  sleeveThickness: number;
  sleeveThicknessOptions: number[];
  onCoreOdMinChange: (value: number | null) => void;
  onCoreOdMaxChange: (value: number | null) => void;
  onHotOdChange: (value: number | null) => void;
  onSleeveThicknessChange: (value: number) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  /** Whether BYD mode is active (shows single Core OD + Hot OD instead of Min/Max). */
  isByd?: boolean;
  errors?: {
    coreOdMin?: string;
    coreOdMax?: string;
    hotOd?: string;
  };
  /** Dynamic range for core OD min/max fields. */
  coreOdRange?: ParamRange;
}

const CoreODGroup: React.FC<CoreODGroupProps> = ({
  coreOdMin,
  coreOdMax,
  hotOd,
  sleeveThickness,
  sleeveThicknessOptions,
  onCoreOdMinChange,
  onCoreOdMaxChange,
  onHotOdChange,
  onSleeveThicknessChange,
  onMenuClick,
  disabled = false,
  isByd = false,
  errors = {},
  coreOdRange,
}) => {
  const handleThicknessChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, value: number | null) => {
      if (value !== null) onSleeveThicknessChange(value);
    },
    [onSleeveThicknessChange],
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        columnGap: 1.5,
        alignItems: 'center', // key: keep everything on one line and centered
      }}
    >
      {/* Sleeve thickness block (CENTERED with title) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // center title + selector together
          justifyContent: 'center',
          gap: 0.95,
          minWidth: 120,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            lineHeight: 1.1,
            textAlign: 'center',
          }}
        >
          Sleeve thickness [mm]
        </Typography>

        <ToggleButtonGroup
          value={sleeveThickness}
          exclusive
          onChange={handleThicknessChange}
          size="small"
          disabled={disabled}
          sx={{
            height: 26,                
            borderRadius: 999,         
            overflow: 'hidden',
            bgcolor: 'common.white',
            '& .MuiToggleButtonGroup-grouped': {
              borderRadius: 0,
              borderColor: 'divider',
              px: 0.9,
              minWidth: 44,
              fontSize: '0.75rem',
              fontWeight: 700,
              lineHeight: 1,
            },
            '& .MuiToggleButtonGroup-grouped:first-of-type': {
              borderTopLeftRadius: 999,
              borderBottomLeftRadius: 999,
            },
            '& .MuiToggleButtonGroup-grouped:last-of-type': {
              borderTopRightRadius: 999,
              borderBottomRightRadius: 999,
            },
          }}
        >
          {sleeveThicknessOptions.map((opt) => (
            <ToggleButton
              key={opt}
              value={opt}
              sx={{
                // Unselected => strike-through
                textDecoration: 'line-through',
                textDecorationThickness: '2px',
                textDecorationColor: 'currentColor',
                opacity: 0.9,

                // Selected => normal
                '&.Mui-selected': {
                  textDecoration: 'none',
                  opacity: 1,
                },
              }}
            >
              {opt}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* BYD mode: single Core OD + Hot OD fields (matches optibush.py) */}
      {isByd ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
          }}
        >
          <NumericTextField
            label="Core OD [mm]"
            value={coreOdMin}
            onChange={onCoreOdMinChange}
            disabled={disabled}
            error={!!errors.coreOdMin}
            helperText={errors.coreOdMin}
            size="small"
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': { borderRadius: 1.25 },
            }}
          />
          <NumericTextField
            label="Hot OD [mm]"
            value={hotOd}
            onChange={onHotOdChange}
            disabled={disabled}
            error={!!errors.hotOd}
            helperText={errors.hotOd}
            size="small"
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': { borderRadius: 1.25 },
            }}
          />
        </Box>
      ) : (
        /* Standard mode: Core OD fieldset with Min/Max */
        <Box
          component="fieldset"
          sx={{
            m: 0,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 1.5,
            py: 1.15,
          }}
        >
          <Box
            component="legend"
            sx={{
              px: 1,
              fontSize: '0.75rem',
              color: 'text.secondary',
              ml: 0.5,
            }}
          >
            Core OD [mm]
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: 1,
              alignItems: 'center',
              mt: 0.4,
            }}
          >
            <NumericTextField
              label="Min"
              value={coreOdMin}
              onChange={onCoreOdMinChange}
              disabled={disabled}
              error={!!errors.coreOdMin}
              helperText={errors.coreOdMin}
              min={coreOdRange?.min}
              max={coreOdRange?.max}
              size="small"
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': { borderRadius: 1.25 },
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                userSelect: 'none',
                lineHeight: 1,
              }}
            >
              –
            </Typography>

            <NumericTextField
              label="Max"
              value={coreOdMax}
              onChange={onCoreOdMaxChange}
              disabled={disabled}
              error={!!errors.coreOdMax}
              helperText={errors.coreOdMax}
              min={coreOdRange?.min}
              max={coreOdRange?.max}
              size="small"
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': { borderRadius: 1.25 },
              }}
            />
          </Box>
        </Box>
      )}

      {/* 3-dot menu (same row) */}
      <Tooltip title="Configuration">
        <span>
          <IconButton
            size="small"
            onClick={onMenuClick}
            disabled={disabled}
            aria-label="More options"
            sx={{ alignSelf: 'center' }}
          >
            <MoreVertIcon fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default React.memo(CoreODGroup);